import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { parseShipmentDayjs } from '@/common/utils/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

const ISO_INSTANT = /[zZ]$|[+-]\d{2}:\d{2}$/;
const TRAILING_TZ_ABBR = /\s(?:[A-Z]{2,5}|GMT[+-]\d{1,2}(?::\d{2})?)$/i;

const parsePublishedAt = (
  date: string,
  userTz: string,
  dateFormat?: string | null
): dayjs.Dayjs | null => {
  const trimmed = date.trim();

  // Real instants (ISO / offset / Z). Converting via naive local wall-clock
  // would shift when the browser TZ differs from userTz.
  if (ISO_INSTANT.test(trimmed)) {
    const instant = dayjs(trimmed).tz(userTz);
    return instant.isValid() ? instant : null;
  }

  // GxP labels are "%Y-%m-%d %H:%M %Z" (year-first, then #4452 may reorder).
  // Year-first + trailing abbr was the pre-#4452 shape — keep dayjs().tz().
  if (
    /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(trimmed) &&
    TRAILING_TZ_ABBR.test(trimmed)
  ) {
    const legacy = dayjs(trimmed).tz(userTz);
    if (legacy.isValid()) return legacy;
  }

  const wallClock = trimmed.replace(TRAILING_TZ_ABBR, '');
  const naive = parseShipmentDayjs(wallClock, dateFormat);
  if (!naive) return null;
  const localized = dayjs.tz(naive.format('YYYY-MM-DDTHH:mm:ss'), userTz);
  return localized.isValid() ? localized : null;
};

export const formatRelativeOrAbsoluteDate = (
  date?: string,
  userTz = dayjs.tz.guess(),
  dateFormat?: string | null
) => {
  if (!date) return '-';

  const publishedAt = parsePublishedAt(date, userTz, dateFormat);
  if (!publishedAt) return date;

  const now = dayjs().tz(userTz);
  const diffHours = now.diff(publishedAt, 'hour');

  if (diffHours < 48) {
    return `${Math.max(diffHours, 1)}h ago`;
  }

  // Field order follows the user's date_format preference; separator (dash)
  // and 24-hour time are kept as-is, matching the existing precise/technical
  // display convention. YYYY/MM/DD and the unset default both resolve to the
  // original year-first ordering.
  const datePart =
    dateFormat === 'DD/MM/YYYY'
      ? 'DD-MM-YYYY'
      : dateFormat === 'MM/DD/YYYY'
        ? 'MM-DD-YYYY'
        : 'YYYY-MM-DD';
  return publishedAt.format(`${datePart} HH:mm z`);
};
